/**
 * RBAC Hook for Role-Based Access Control
 *
 * Provides utilities to check user permissions based on Azure AD App Roles.
 * Used for navigation filtering and conditional UI rendering.
 */

import { useMemo, useCallback } from 'react'
import { useAuth } from '@/context/AuthContext'
import { ROLES, type RoleName, type NavPermission } from '@/lib/roles'

interface RBACResult {
  /** User's roles from Azure AD */
  roles: string[]
  /** Whether user has Admin role (full access) */
  isAdmin: boolean
  /** Whether auth is still loading */
  isLoading: boolean
  /** Check if user has a specific role */
  hasRole: (role: RoleName) => boolean
  /** Check if user has any of the specified roles (OR logic) */
  hasAnyRole: (...roles: RoleName[]) => boolean
  /** Check if user has all of the specified roles (AND logic) */
  hasAllRoles: (...roles: RoleName[]) => boolean
  /** Check if user can access based on NavPermission */
  canAccess: (permission: NavPermission | undefined) => boolean
}

/**
 * Hook to check user roles and permissions
 *
 * @example
 * ```tsx
 * const { canAccess, hasRole, isAdmin } = useRBAC()
 *
 * // Check specific role
 * if (hasRole(ROLES.EQUIP_PHYSICIAN_LEAD)) {
 *   // Show physician-specific features
 * }
 *
 * // Check any of multiple roles
 * if (hasAnyRole(ROLES.EQUIP_ADMIN, ROLES.EQUIP_APC_LEAD)) {
 *   // Show APC features
 * }
 *
 * // Admin always has access
 * if (isAdmin) {
 *   // Show admin features
 * }
 * ```
 */
export function useRBAC(): RBACResult {
  const { user, isAuthenticated, isLoading } = useAuth()

  const roles = useMemo<string[]>(() => {
    return user?.roles ?? []
  }, [user?.roles])

  const isAdmin = useMemo(() => {
    // Accept either global Admin or EQuIP-specific admin role
    return roles.includes(ROLES.ADMIN) || roles.includes(ROLES.EQUIP_ADMIN)
  }, [roles])

  const hasRole = useCallback(
    (role: RoleName): boolean => {
      // Admin bypasses all role checks
      if (isAdmin) return true
      return roles.includes(role)
    },
    [roles, isAdmin]
  )

  const hasAnyRole = useCallback(
    (...checkRoles: RoleName[]): boolean => {
      // Admin bypasses all role checks
      if (isAdmin) return true
      return checkRoles.some((role) => roles.includes(role))
    },
    [roles, isAdmin]
  )

  const hasAllRoles = useCallback(
    (...checkRoles: RoleName[]): boolean => {
      // Admin bypasses all role checks
      if (isAdmin) return true
      return checkRoles.every((role) => roles.includes(role))
    },
    [roles, isAdmin]
  )

  const canAccess = useCallback(
    (permission: NavPermission | undefined): boolean => {
      // No permission specified - default to requiring authentication
      if (permission === undefined) {
        return isAuthenticated
      }

      // "authenticated" means any logged-in user
      if (permission === 'authenticated') {
        return isAuthenticated
      }

      // Must be authenticated for any role-based check
      if (!isAuthenticated) {
        return false
      }

      // Admin always has access
      if (isAdmin) {
        return true
      }

      // Single role check
      if (typeof permission === 'string') {
        return roles.includes(permission)
      }

      // Array of roles - user needs ANY of them (OR logic)
      if (Array.isArray(permission)) {
        return permission.some((role) => roles.includes(role))
      }

      return false
    },
    [roles, isAdmin, isAuthenticated]
  )

  return {
    roles,
    isAdmin,
    isLoading,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    canAccess,
  }
}
